import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useReducer,
} from "react";

import {
  Button,
  Card,
  CardActions,
  CardContent,
  Box,
  Fade,
  Typography,
  Avatar,
} from "@mui/material";

import {
  Phone as PhoneIcon,
  CallEnd as CallEndIcon,
  Person as PersonIcon,
} from '@mui/icons-material';

import { useLocation } from 'react-router-dom'; // Add this import if using React Router

import api from "../../api";

import { getSession } from "../../auth";
import { useTelephony } from "../../contexts/TelephonyContext";
import ErrorBoundary from "../ErrorBoundary";

import "./index.css";
import { QueueSocket } from "../../routes";
import defaultReducer from "../../utils/defaultReducer";

import JsSIP from "jssip/lib/JsSIP";
import formatPhone from "../../utils/format_phone";

import { checkTelephonyTab , formatNumber, getCrmData } from "../Telephony/Utils";
import { useIncomingCallData } from "../Telephony/hooks/useIncomingCallData";
import IncomingCallContent from "../Telephony/IncomingCallContent";

const FILES = require("./sounds.json");

const SOUNDS = new Map([
  ["ringback", { audio: new Audio(FILES["ringback"]), volume: 1.0 }],
  ["ringing", { audio: new Audio(FILES["ringing"]), volume: 1.0 }],
  ["answered", { audio: new Audio(FILES["answered"]), volume: 1.0 }],
  ["rejected", { audio: new Audio(FILES["rejected"]), volume: 0.5 }],
  ["ended", { audio: new Audio(FILES["ended"]), volume: 1.0 }],
]);

function playSound(name, loop = false) {
  const sound = SOUNDS.get(name);
  if (sound) {
    sound.audio.loop = loop;
    sound.audio.play().catch((error) => {
      console.error(`Error playing sound ${name}:`, error);
    });
  }
}

function stopSound(name) {
  const sound = SOUNDS.get(name);
  if (sound) {
    sound.audio.pause();
    sound.audio.currentTime = 0;
  }
}

const Webphone = ({ reAnchor, handleClose, handleLoading }) => {
  const location = useLocation(); // If using React Router
  const { sendNotification } = useContext(QueueSocket);

  // Telephony context for communication with MainPanel
  const {
    phoneSignal,
    dialNumber,
    setDialNumber,
    callStatus,
    updateCallStatus,
    clearSignal,
    incomingCall,
    setRegistered,
    volume,
    muted,
    widgetSignal,
    setWidgetSignal,
    incomingData: contextIncomingData,
    ctiData: contextCtiData,
    uniqueid,
    setIncomingData,
    setCallDirection,
    setUniqueid,
    answerCall: answerCallFromContext,
    setCtiData,
  } = useTelephony();

  const [state, setState] = useReducer(defaultReducer, {
    ready: false,
    socketURI: "",
    configuration: {
      uri: null,
      password: null,
      display_name: "InstantVoice",
      sockets: [],
      registrar_server: null,
      contact_uri: null,
      authorization_user: null,
      instance_id: null,
      session_timers: true,
      use_preloaded_route: false,
    },
    callOptions: {
      pcConfig: {
        rtcpMuxPolicy: "negotiate",
        iceServers: [],
        iceTransportPolicy: "relay",
      },
      mediaConstraints: {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      },
      rtcOfferConstraints: {
        offerToReceiveAudio: 1,
        offerToReceiveVideo: 0,
      },
    },
    ctiData: null,
    incoming: null,
  });

  const [ready, setReady] = useState(false);
  const [isMounted, setIsMounted] = useState(false); // Add this flag

  const [ua, setUa] = useState(null);
  const [session, setSession] = useState(null);

  const user = getSession();
  const buttonRef = useRef(null);
  const remoteStream = useRef(null);
  const localStream = useRef(null);
  const uaRef = useRef(null);
  const turnConfigRef = useRef({ allServers: null, tcpOnly: null });
  const audioCheckTimerRef = useRef(null);
  const tcpFallbackAttemptedRef = useRef(!!sessionStorage.getItem('turnFallbackTcp'));
  const isOnHoldRef = useRef(false);
  const rtpStrategyRef = useRef('Auto');

  const has_voice =
    user.profile.enterprise.has_voice ||
    user.profile.enterprise.whatsapp_calling;

  const fetchData = (exten) => {
    api
      .get("/api/WEBRTC/ps_auths/" + exten + "/get_exten/")
      .then((res) => {
        const configuration = {
          ...state.configuration,
          uri: res.data.sip_uri,
          password: res.data.password,
        };
        const rtp_strategy_account = user.profile.enterprise.rtp_strategy || 'Auto';
        const rtp_strategy_user = user.profile.agent.rtp_strategy || 'Parent';
        let computed_rtp_strategy = 'Auto';
        if (rtp_strategy_account !== 'Auto') {
          console.log(`[RTP Strategy] Applying account-level RTP strategy: ${rtp_strategy_account}`);
          computed_rtp_strategy = rtp_strategy_account;
        } else if (rtp_strategy_user !== 'Parent') {
          console.log(`[RTP Strategy] Overriding account-level with user-level RTP strategy: ${rtp_strategy_user}`);
          computed_rtp_strategy = rtp_strategy_user;
        }
        rtpStrategyRef.current = computed_rtp_strategy;

        const callOptions = state.callOptions;
        const turnCredentials = {
          username: res.data.turn_username,
          credential: res.data.turn_password,
        };

        switch (computed_rtp_strategy) {
          case 'UDP':
            callOptions.pcConfig.iceServers = [{
              urls: [res.data.turn_udp],
              ...turnCredentials,
            }];
            break;
          case 'TCP':
          case 'TCP/443':
            callOptions.pcConfig.iceServers = [{
              urls: [res.data.turn_tcp],
              ...turnCredentials,
            }];
            break;
          case 'Mixed':
            callOptions.pcConfig.iceServers = [{
              urls: [res.data.turn_udp, res.data.turn_tcp],
              ...turnCredentials,
            }];
            break;
          case 'Auto':
          default:
            turnConfigRef.current = {
              allServers: [{
                urls: [res.data.turn_udp, res.data.turn_tcp],
                ...turnCredentials,
              }],
              tcpOnly: [{
                urls: [res.data.turn_tcp],
                ...turnCredentials,
              }],
            };
            // Default to TCP (safe for inbound calls before UDP is verified)
            if (sessionStorage.getItem('turnUdpVerified')) {
              callOptions.pcConfig.iceServers = turnConfigRef.current.allServers;
            } else {
              callOptions.pcConfig.iceServers = turnConfigRef.current.tcpOnly;
            }
            break;
        }
        console.log(`[RTP Strategy] ${computed_rtp_strategy} - Configured ICE servers:`, callOptions.pcConfig.iceServers);

        setState({
          socketURI: res.data.ws_uri,
          configuration: configuration,
          callOptions: callOptions,
        });

        setTimeout(() => {
          if (buttonRef.current) {
            buttonRef.current.click();
            setTimeout(() => {
              handleClose();
            }, 500);
          }
        }, 1500);
      })
      .catch((err) => {
        console.error("[Config] Failed to fetch WebRTC configuration:", err);
      });
  };

  const handleRemoteStream = (stream) => {

    if (remoteStream.current && stream) {
      remoteStream.current.srcObject = stream;
      // Ao receber/atualizar o stream, refletir volume/mute atuais
      try {
        remoteStream.current.volume = Number(volume ?? 1);
        remoteStream.current.muted = false;
      } catch { }
      stream.addEventListener("addtrack", (event) => {
        // Refresh remote video
        remoteStream.current.srcObject = stream;
      });

      stream.addEventListener("removetrack", () => {
        // Refresh remote video
        remoteStream.current.srcObject = stream;
      });
    } else if (remoteStream.current && !stream) {
      // Clear the stream if null is passed
      remoteStream.current.srcObject = null;
    }
  };


  const stopAudioMonitoring = () => {
    if (audioCheckTimerRef.current) {
      clearTimeout(audioCheckTimerRef.current);
      audioCheckTimerRef.current = null;
    }
  };

  const startAudioMonitoring = (_session) => {
    stopAudioMonitoring();

    // if it's already on hold, don't even schedule the monitor
    if (isOnHoldRef.current) return;

    audioCheckTimerRef.current = setTimeout(async () => {
      // if it went on hold during the timer, don't drop the call
      if (isOnHoldRef.current) return;

      const pc = _session?.connection;
      if (!pc || pc.connectionState === "closed") return;

      try {
        const stats = await pc.getStats();
        let bytesReceived = 0;

        stats.forEach((report) => {
          if (report.type === "inbound-rtp" && report.kind === "audio") {
            bytesReceived += report.bytesReceived || 0;
          }
        });

        console.log(`[Audio Monitor] bytesReceived: ${bytesReceived}, connectionState: ${pc.connectionState}`);

        if (bytesReceived === 0) {
          console.warn("[Audio Monitor] No audio via UDP after 5s — switching to TCP and redialing");
          tcpFallbackAttemptedRef.current = true;
          sessionStorage.setItem('turnFallbackTcp', '1');
          sessionStorage.removeItem('turnUdpVerified');

          // Switch ICE config to TCP-only for this and future calls
          state.callOptions.pcConfig.iceServers = turnConfigRef.current.tcpOnly;

          // Capture call info before terminating
          const isOutbound = _session.direction === "outgoing";
          const number = _session.remote_identity?.uri?.user;

          _session.terminate();

          // Auto-redial outbound calls with TCP-only
          if (isOutbound && number) {
            setTimeout(() => {
              console.log("[Audio Monitor] Auto-redialing with TCP TURN:", number);
              makeCall(number);
            }, 1000);
          } else {
            console.warn("[Audio Monitor] Incoming call — cannot auto-redial. Future calls will use TCP.");
          }
        } else {
          console.log("[Audio Monitor] Audio OK via UDP — verified for future calls");
          sessionStorage.setItem('turnUdpVerified', '1');
          sessionStorage.removeItem('turnFallbackTcp');
        }
      } catch (err) {
        console.error("[Audio Monitor] Stats error:", err);
      }
    }, 5000);
  };

  const handlePeerConnection = (peerconnection) => {

    if (!peerconnection) {
      return;
    }

    // === NEW: ICE Candidate Event Logging ===
    peerconnection.addEventListener("icecandidate", (event) => {
      if (event.candidate) {
        console.log("[ICE] New candidate gathered:", {
          type: event.candidate.type,           // host, srflx, relay
          protocol: event.candidate.protocol,   // udp, tcp
          address: event.candidate.address,
          port: event.candidate.port,
          priority: event.candidate.priority,
          candidate: event.candidate.candidate  // Full SDP
        });
      } else {
        console.log("[ICE] ✓ All candidates gathered (end-of-candidates)");
      }
    });

    // === NEW: ICE Gathering State Tracking ===
    peerconnection.addEventListener("icegatheringstatechange", () => {
      const state = peerconnection.iceGatheringState;
      console.log(`[ICE Gathering] State changed: ${state}`);

      if (state === "gathering") {
        console.log("[ICE Gathering] Started gathering candidates...");
        console.time("ICE-gathering-duration");
      } else if (state === "complete") {
        console.timeEnd("ICE-gathering-duration");
        console.log("[ICE Gathering] ✓ Completed");
      }
    });

    // === NEW: ICE Connection State Tracking ===
    peerconnection.addEventListener("iceconnectionstatechange", () => {
      const state = peerconnection.iceConnectionState;
      console.log(`[ICE Connection] State changed: ${state}`);

      if (state === "checking") {
        console.log("[ICE Connection] Checking connectivity...");
        console.time("ICE-connection-duration");
      } else if (state === "connected" || state === "completed") {
        console.timeEnd("ICE-connection-duration");
        console.log("[ICE Connection] ✓ Connected successfully");
      } else if (state === "failed") {
        console.error("[ICE Connection] ✗ FAILED - No connectivity");
        console.timeEnd("ICE-connection-duration");
      } else if (state === "disconnected") {
        console.warn("[ICE Connection] ⚠ Disconnected");
      } else if (state === "closed") {
        console.log("[ICE Connection] Closed");
      }
    });

    // === NEW: Connection State Tracking ===
    peerconnection.addEventListener("connectionstatechange", () => {
      console.log(`[PeerConnection] State: ${peerconnection.connectionState}`);
    });
 
    // === NEW: Signaling State Tracking ===
    peerconnection.addEventListener("signalingstatechange", () => {
      console.log(`[Signaling] State: ${peerconnection.signalingState}`);
    });

    peerconnection.addEventListener("addstream", (event) => {
      handleRemoteStream(event.stream);
    });

    peerconnection.addEventListener("removestream", (event) => {
      handleRemoteStream(null);
    });

    // Moderno: evento 'track' (alguns browsers disparam só ele)
    const onTrack = (event) => {
      const stream = event.streams?.[0];
      if (stream) handleRemoteStream(stream);
    };
    peerconnection.addEventListener("track", onTrack);

    const l = peerconnection.getLocalStreams()[0];
    const r = peerconnection.getRemoteStreams()[0];
    if (l) {
      localStream.current.srcObject = l;
      try {
        localStream.current.muted = true;      // garante mudo
        localStream.current.volume = 0;
      } catch { }
    }
    if (r) {
      handleRemoteStream(r);
    }

  };

  const bindSessionEvents = (_session) => {

    _session.on("connecting", () => {
      console.log("[Session] connecting...");
      console.time("session-connecting-to-progress");
      setSession(_session);
    });

    _session.on("progress", () => {
      console.log("[Session] progress (ringing)...");
      console.timeEnd("session-connecting-to-progress");
      console.time("session-progress-to-accepted");
      playSound("ringback", true);
    });

    _session.on("failed", (data) => {
      if (audioCheckTimerRef.current) {
        clearTimeout(audioCheckTimerRef.current);
        audioCheckTimerRef.current = null;
      }
      console.error("[Session] FAILED");
      console.error("[Session] Failure cause:", data.cause);
      console.error("[Session] Failure message:", data.message);
      console.error("[Session] Full data:", data);
      console.timeEnd("call-setup-duration");

      const ringback = SOUNDS.get("ringback");
      const tt = checkTelephonyTab();
      ringback.audio.pause();
      stopSound("ringback");
      playSound("rejected", false);
      updateCallStatus("idle", tt);
      setDialNumber("");
      setUniqueid(null);
      setCallDirection(null);
      handleRemoteStream(null);
      setSession(null);

    });

    _session.on("ended", () => {
      if (audioCheckTimerRef.current) {
        clearTimeout(audioCheckTimerRef.current);
        audioCheckTimerRef.current = null;
      }
      console.log("[Session] ended");
      console.timeEnd("call-setup-duration");

      const ringback = SOUNDS.get("ringback");
      const tt = checkTelephonyTab();
      ringback.audio.pause();
      playSound("ended", false);
      updateCallStatus("idle", tt);
      setDialNumber("");
      setUniqueid(null);
      setCallDirection(null);
      handleRemoteStream(null);
      setSession(null);
    });

    _session.on("accepted", (data) => {
      console.log("[Session] ✓ ACCEPTED (call connected)");
      console.timeEnd("session-progress-to-accepted");
      console.timeEnd("call-setup-duration");

      const ringback = SOUNDS.get("ringback");
      ringback.audio.pause();
      const a = SOUNDS.get("answered");
      a.audio.volume = 1.0;
      a.audio.loop = false;
      a.audio.play();
      updateCallStatus("connected");
      handlePeerConnection(_session.connection);
      // Garante que, ao conectar, o elemento respeite volume/mute atuais
      if (remoteStream.current) {
        try {
          remoteStream.current.volume = Number(volume ?? 1);
          remoteStream.current.muted = Boolean(muted);
        } catch { }
      }
      // Entrada: aplicar estado atual do mute no microfone
      try {
        const pc = _session.connection;
        const locals = pc?.getLocalStreams?.() ?? [];
        locals.forEach(stream =>
          stream.getAudioTracks().forEach(t => (t.enabled = !muted))
        );
      } catch { }

      // Monitor audio — only when strategy is Auto
      if (rtpStrategyRef.current === 'Auto') {
        if (!tcpFallbackAttemptedRef.current) {
          startAudioMonitoring(_session);
        } else {
          console.log("[Audio Monitor] Using TCP TURN (previous fallback), skipping monitor");
        }
      }
    });

    _session.on("hold", () => {
      isOnHoldRef.current = true;      
      stopAudioMonitoring();
    });

    _session.on("unhold", () => {
      isOnHoldRef.current = false;
    });
    _session.on("muted", (data) => {});
    _session.on("unmuted", (data) => {});

   handlePeerConnection(_session.connection);
  };

  // Call this to make a call
  const makeCall = (number, retries = 5, delay = 1000) => {
    if (!uaRef.current) {
      console.warn(`UA is not initialized yet, waiting ${delay}ms... (${retries} retries left)`);
      if (retries > 0) {
        setTimeout(() => makeCall(number, retries - 1, delay), delay);
        return;
      } else {
        console.error("UA is not initialized after multiple retries");
        return;
      }
    }

    // Outbound calls (Auto only): try UDP (we can redial with TCP if it fails)
    // Unless we already know UDP is broken on this network
    if (rtpStrategyRef.current === 'Auto' && !tcpFallbackAttemptedRef.current && turnConfigRef.current.allServers) {
      state.callOptions.pcConfig.iceServers = turnConfigRef.current.allServers;
    } 

    console.log("=== OUTBOUND CALL INITIATED ===");
    console.log(`[Call] Dialing: ${number}`);
    console.log("[Call] RTP strategy:", rtpStrategyRef.current);
    console.log("[Call] TURN mode:", tcpFallbackAttemptedRef.current ? "TCP-only" : "UDP+TCP");
    console.time("call-setup-duration");

    const _session = uaRef.current.call(number, state.callOptions);

    bindSessionEvents(_session);
  };

  const hangupClick = () => {
    if (session) {
      session.terminate();
      setSession(null);
    }
    return;
  };

  const startWebphone = () => {
    // JsSIP.debug.enable("JsSIP:*");
    JsSIP.debug.disable();

    var socket = new JsSIP.WebSocketInterface(state.socketURI);
    const config = {
      ...state.configuration,
      sockets: [socket],
    };
    const _ua = new JsSIP.UA(config);
    
    _ua.on("newRTCSession", (data) => {

      if (data.originator === "local") {
        return;
      }

      const _session = data.session;
      if (state.session) {
        console.log('incoming call replied with 486 "Busy Here"');

        _session.terminate({
          status_code: 486,
          reason_phrase: "Busy Here",
        });

        return;
      }

      bindSessionEvents(_session);
      setSession(_session);

      const phone_number = data.request.from.uri.user;
      const display_name = data.request.from.display_name;

      const headers = data.request.headers;
      const info = {};
      let internalCall = false;
      if ( headers['X-Instantvoice-Extensionid'] && headers['X-Instantvoice-Extensionid'].length > 0 ) {
        internalCall = true;
        info.extension_id = headers['X-Instantvoice-Extensionid'][0].raw.replace('X-Instantvoice-Extensionid: ','');
      }
      if ( headers['X-Instantvoice-Extensiondescription'] && headers['X-Instantvoice-Extensiondescription'].length > 0 ) {
        internalCall = true;
        info.extension_description = headers['X-Instantvoice-Extensiondescription'][0].raw.replace('X-Instantvoice-Extensiondescription: ','');
      }
      if ( headers['X-Instantvoice-Agentid'] && headers['X-Instantvoice-Agentid'].length > 0 ) {
        internalCall = true;
        info.agent_id = headers['X-Instantvoice-Agentid'][0].raw.replace('X-Instantvoice-Agentid: ','');
      }

      if ( headers['X-Instantvoice-Agentname'] && headers['X-Instantvoice-Agentname'].length > 0 ) {
        internalCall = true;
        info.agent_name = headers['X-Instantvoice-Agentname'][0].raw.replace('X-Instantvoice-Agentname: ','');
      }

      if ( headers['X-Instantvoice-Username'] && headers['X-Instantvoice-Username'].length > 0 ) {
        internalCall = true;
        info.agent_username = headers['X-Instantvoice-Username'][0].raw.replace('X-Instantvoice-Username: ','');
      }

      if ( headers['X-Instantvoice-Uniqueid'] && headers['X-Instantvoice-Uniqueid'].length > 0 ) {
        info.uniqueid = headers['X-Instantvoice-Uniqueid'][0].raw.replace('X-Instantvoice-Uniqueid: ','');
      }
      const exten = internalCall ? phone_number : formatPhone(phone_number);

      info.internalCall = internalCall;
      info.callType = "INBOUND";

      const tt = checkTelephonyTab();
      incomingCall(exten, info, tt);
      if (!tt) {
        reAnchor();
      }
      sendNotification("newCall", 1, 1);
    });

    _ua.on("registered", (data) => {
      setRegistered(true);
    });

    _ua.on("connecting", (data) => {
    });

    _ua.on("connected", (data) => {
    });

    _ua.on("disconnected", (data) => {
      setRegistered(false);
    });

    _ua.on("registrationFailed", (data) => {
      setRegistered(false);
    });

    _ua.on("unregistered", (data) => {
      setRegistered(false);
    });

    _ua.start();
    setUa(_ua);
    uaRef.current = _ua;

    setReady(true);
    handleLoading(false);
    reAnchor();
  };

  const answerCall = () => {
    if (session) {
      // Check if session is incoming and not yet answered
      if (session.direction === 'incoming' && !session.isEstablished()) {
        // Inbound Auto mode: use TCP unless UDP has been verified by a prior outbound call
        if (rtpStrategyRef.current === 'Auto' && (!sessionStorage.getItem('turnUdpVerified') || tcpFallbackAttemptedRef.current)) {
          state.callOptions.pcConfig.iceServers = turnConfigRef.current.tcpOnly;
        }
        session.answer(state.callOptions);
        return true;
      }
    }
    return false;
  };

  // Reagir a mudanças no contexto (volume/muted) e aplicar no elemento remoto
  useEffect(() => {
    const el = remoteStream.current;
    if (!el) return;
    try {
      el.volume = Number(volume ?? 1);   // 0..1
      //el.muted = Boolean(muted);
    } catch { }
  }, [volume]);

  // Reagir a mudanças de MUTE (entrada): liga/desliga microfone local
  useEffect(() => {
    try {
      const pc = session?.connection;
      if (!pc) return;
      const locals = pc.getLocalStreams?.() ?? [];
      locals.forEach(stream =>
        stream.getAudioTracks().forEach(t => (t.enabled = !muted))
      );
    } catch { }
  }, [muted, session]);

  // Effect to listen for telephony signals from MainPanel
  useEffect(() => {
    if (phoneSignal) {

      switch (phoneSignal.action) {
        case "dial":
          // Update webphone call status
          updateCallStatus("dialing");
          makeCall(phoneSignal.number);
          break;

        case "hangup":
          hangupClick();
          console.log("Hanging up call");
          const tt = checkTelephonyTab();
          updateCallStatus("idle", tt);
          break;

        case "answer":
          if (answerCall()) {
            console.log("Answering call");
            updateCallStatus("connected");
          } else {
            console.log("No incoming call to answer");
            const tt = checkTelephonyTab();
            updateCallStatus("idle", tt);
          }
          break;

        case "dtmf":
          if (session) {
            console.log("Sending DTMF digit:", phoneSignal.digit);
            for (let i = 0; i < phoneSignal.digit.length; i++) {
              console.log("Sending DTMF digit char:", phoneSignal.digit.charAt(i));
              const digit = phoneSignal.digit.charAt(i);
              session.sendDTMF(digit);
            }
          } else {
            console.log("No active session to send DTMF");
          }
          break;

        case "hold":
          if (session) {
            console.log("Putting call on hold");
            session.hold();
          }
          break;

        case "unhold":
          if (session) {
            console.log("Resuming call from hold");
            session.unhold();
          }
          break;


        default:
          console.log("Unknown telephony action:", phoneSignal.action);
      }

      // Clear the signal after processing
      clearSignal();
    }
  }, [phoneSignal, updateCallStatus, clearSignal]);

  useEffect(async () => {
    if (widgetSignal) {
      console.log("Webphone received widget signal:", widgetSignal);
      switch (widgetSignal.action) {
        case "incoming":
          setState({ incoming: { number: widgetSignal.number, info: widgetSignal.info } });
          setIncomingData({ number: widgetSignal.number, info: widgetSignal.info });
          // CRM data fetching now handled by useIncomingCallData hook
          break;
        case "cti":
          setState({ ctiData: widgetSignal });
          setCtiData(widgetSignal);
          
          // Handle incoming and CTI signals
          break;
        case "idle":
          setState({ incoming: null, ctiData: null });
          setIncomingData(null);
          setCtiData(null);
          handleClose();
          break;

        default:
          console.log("Unknown widget action:", widgetSignal.action);
      }
      // Clear the signal after processing
      setWidgetSignal(null);
    }
}, [widgetSignal, setWidgetSignal]);


  useEffect(() => {
    if (callStatus.status === "idle") {
      setState({ incoming: null, ctiData: null });
    }
  }, [callStatus.status]);

const widgetIncomingData = state.incoming;
  const incomingData = (callStatus.status === "ringing" || callStatus.status === "connected")
    ? (contextIncomingData || widgetIncomingData)
    : null;
  const ctiData = contextCtiData || state.ctiData;
  const incomingCallUniqueid = incomingData?.info?.uniqueid || uniqueid;

  // Use custom hook for CRM data fetching and display logic
  const { crmData, loading, error, getDisplayName, getInitials } = useIncomingCallData(incomingData, incomingCallUniqueid);

  // Blue color scheme for webphone widget
  const colorScheme = {
    primary: '#21446C',
    secondary: '#5399DB',
    text: 'white',
    chipBg: 'rgba(255, 255, 255, 0.15)',
    chipText: 'white'
  };

  if (has_voice) {
    if (user.profile.agent.preferred_interface) {
      // Effect to set mounted flag
      useEffect(() => {
        setIsMounted(true);
        return () => setIsMounted(false); // Cleanup on unmount
      }, []);

      // Modified effect for fetchData
      useEffect(() => {
        if (isMounted && user.profile.agent.preferred_interface) {
          const exten = user.profile.agent.preferred_interface.split("SIP/")[1];
          fetchData(exten);
        }
      }, [isMounted]); // Only depend on isMounted

      return (
        <ErrorBoundary
          componentName="Webphone"
          title="Erro no Webphone"
          message="Erro no webphone. Por favor, recarregue a página."
          showReload={true}
          logToBackend={true}
        >
          <Fade in={true} timeout={300}>
            <Box className="webphone-widget-container">
            <Card
              className={`webphone-enhanced-card ${callStatus.status}`}
              elevation={8}
              sx={{ 
                minWidth: 320, 
                maxWidth: 400,
                background: 'linear-gradient(135deg, #21446C 0%, #5399DB 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'visible'
              }}
            >

              <CardContent sx={{ pb: 1 }}>
                {incomingData ? (
                  <IncomingCallContent
                    crmData={crmData}
                    incomingData={incomingData}
                    ctiData={ctiData}
                    loading={loading}
                    error={error}
                    colorScheme={colorScheme}
                    showError={true}
                    showAvatar={true}
                    getDisplayName={getDisplayName}
                    getInitials={getInitials}
                  />
                ) : (callStatus.status === 'dialing' || callStatus.status === 'connected') && dialNumber ? (
                  <Box sx={{ py: 2, textAlign: 'center' }}>
                    <Avatar
                      sx={{
                        width: 100,
                        height: 100,
                        mx: 'auto',
                        mb: 3,
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        border: '3px solid rgba(255, 255, 255, 0.3)',
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 50 }} />
                    </Avatar>
                    <Typography
                      variant="subtitle1"
                      sx={{ color: 'white', mb: 1, opacity: 0.9 }}
                    >
                      {callStatus.status === 'dialing' ? 'Ligando...' : 'Em chamada'}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        color: 'white',
                        fontFamily: 'monospace',
                        fontWeight: 'bold',
                        letterSpacing: 1,
                        textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      }}
                    >
                      {dialNumber}
                    </Typography>
                  </Box>
                ) : null}
              </CardContent>

              {/* Action Buttons */}
              <CardActions
                sx={{
                  justifyContent: 'center',
                  gap: 2,
                  pb: 3,
                  pt: 2
                }}
              >
                {incomingData && callStatus.status !== "connected" && (
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<PhoneIcon />}
                    onClick={() => {
                      answerCall();
                      answerCallFromContext(incomingData.number); // Update context state
                      setTimeout(() => {
                        handleClose();
                      }, 500);
                    }}
                    className="webphone-answer-button"
                    sx={{
                      backgroundColor: '#4CAF50',
                      '&:hover': {
                        backgroundColor: '#45A049'
                      },
                      borderRadius: '25px',
                      px: 3,
                      py: 1,
                      fontWeight: 600,
                      boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)'
                    }}
                  >
                    Atender
                  </Button>
                )}

                <Button
                  variant="contained"
                  size="large"
                  startIcon={<CallEndIcon />}
                  onClick={() => {
                    hangupClick();
                    setTimeout(() => {
                      handleClose();
                    }, 500);
                  }}
                  className="webphone-hangup-button"
                  sx={{
                    backgroundColor: '#F44336',
                    '&:hover': {
                      backgroundColor: '#E53935'
                    },
                    borderRadius: '25px',
                    px: 3,
                    py: 1,
                    fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(244, 67, 54, 0.4)'
                  }}
                >
                  {callStatus.status === "connected" ? "Desligar" : incomingData ? "Recusar" : "Cancelar"}
                </Button>
              </CardActions>

              {/* Hidden Audio Elements */}
              <video
                ref={remoteStream}
                autoPlay
                style={{ display: 'none' }}
              />
              <video
                ref={localStream}
                autoPlay
                muted
                playsInline
                style={{ display: 'none' }}
              />
            </Card>

            {/* Initialization Button */}
            {!ready && (
              <Fade in={true} timeout={500}>
                <Box mt={2} textAlign="center">
                  <Button 
                    variant="contained" 
                    onClick={startWebphone} 
                    ref={buttonRef}
                    size="large"
                    sx={{
                      backgroundColor: '#21446C',
                      '&:hover': {
                        backgroundColor: '#1A3558'
                      },
                      borderRadius: '25px',
                      px: 4,
                      py: 1.5,
                      fontWeight: 600
                    }}
                  >
                    Iniciar Webphone
                  </Button>
                </Box>
              </Fade>
            )}
            </Box>
          </Fade>
        </ErrorBoundary>
      );
    } else {
      return <div style={{ padding: "5px" }}>Ramal não configurado.</div>;
    }
  } else return <div style={{ padding: "5px" }}>Voz não contratada.</div>;
};

export default Webphone;
